
import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiUrl } from "../../config";
import { FullDetails } from "../../design-system/components/FullDetails";
import { useAuth } from "../../hooks/useAuth";

/**
 * FACEIT Data API: Player Search
 * This page calls your backend wrapper for GET /players?nickname=...
 * What you get without fetching: a paged list with keys like items[], start, end, total_count (and each player’s basic info).
 */
export function SearchPlayers() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [nickname, setNickname] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);
  const autoSearched = useRef(false);

  async function search(query?: string) {
    setErr(null);
    setData(null);
    const q = (query ?? nickname).trim();
    if (!q) return;
    setLoading(true);
    try {
      const url = apiUrl(`/api/faceit/players/search?nickname=${encodeURIComponent(q)}`);
      const res = await fetch(url);
      const json = await res.json();
      if (!res.ok || json?.error) setErr(json?.error || `HTTP ${res.status}`);
      else setData(json);
    } catch (e: any) {
      setErr(e?.message || "Network error");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const q = params.get("nickname");
    if (q) {
      setNickname(q);
      void search(q);
    }
  }, [params]);

  useEffect(() => {
    if (autoSearched.current) return;
    const faceitNick = user?.faceit?.nickname;
    if (!faceitNick) return;
    autoSearched.current = true;
    setNickname(faceitNick);
    void search(faceitNick);
  }, [user]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold">Search FACEIT Players by Nickname</h2>
        <p className="text-sm text-muted">
          Returns a paged list of matching players with <code>items</code>, <code>start</code>, <code>end</code>, <code>total_count</code>.
        </p>
      </div>

      {/* Form */}
      <div className="bg-surface border border-border rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-2">
          <input
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder="nickname (e.g. s1mple)"
            className="flex-1 bg-bg border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#4FC3F7]"
          />
          <button
            type="button"
            onClick={() => void search()}
            disabled={!nickname || loading}
            className="px-3 py-2 rounded-md border border-border hover:border-[#4FC3F7] hover:text-[#4FC3F7] transition text-sm"
          >
            {loading ? "Searching…" : "Search"}
          </button>
        </div>

        {err && <pre className="text-xs text-red-400 overflow-x-auto">{err}</pre>}
      </div>

      {/* Result */}
      {data && (
        <div className="space-y-4">
          {(() => {
            const items = Array.isArray(data?.items)
              ? data.items
              : data?.player_id
                ? [data]
                : [];
            const total = typeof data?.total_count === "number" ? data.total_count : items.length;
            return (
              <>
                <div className="text-sm text-muted">
                  Results: {items.length} / {total || "?"}
                </div>

                {items.length > 0 ? (
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {items.map((p: any) => {
                const games = p?.games || p?.game || {};
                const cs = games?.cs2 || games?.csgo || games?.["cs:go"] || null;
                return (
                  <div
                    key={p?.player_id || p?.nickname}
                    className="rounded-lg border border-border bg-surface p-4"
                  >
                    <div className="flex items-center gap-3">
                      {p?.avatar ? (
                        <img
                          src={p.avatar}
                          alt={p.nickname}
                          className="h-12 w-12 rounded-full border border-border object-cover"
                        />
                      ) : (
                        <div className="h-12 w-12 rounded-full border border-border bg-bg" />
                      )}
                      <div className="min-w-0">
                        <div className="text-base font-semibold truncate">{p?.nickname}</div>
                        <div className="text-xs text-muted truncate">
                          {p?.country || "Unknown country"}
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                      <div className="rounded-md bg-bg px-2 py-1 border border-border">
                        <div className="text-muted">Player ID</div>
                        <div className="truncate">{p?.player_id || "—"}</div>
                      </div>
                      <div className="rounded-md bg-bg px-2 py-1 border border-border">
                        <div className="text-muted">Skill (CS)</div>
                        <div className="truncate">
                          {cs?.skill_level ?? "—"} / ELO {cs?.faceit_elo ?? "—"}
                        </div>
                      </div>
                    </div>

                    {p?.faceit_url && (
                      <a
                        href={String(p.faceit_url).replace("{lang}", "en")}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-3 inline-block text-xs text-[#4FC3F7] hover:underline"
                      >
                        View FACEIT profile
                      </a>
                    )}

                    <FullDetails data={p} title="All fields" />
                  </div>
                );
                    })}
                  </div>
                ) : (
                  <div className="text-sm text-muted">No results.</div>
                )}
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}
